/**
 * Homepage marketing widget shell and artifact selection.
 *
 * The outer shell classes are intentionally duplicated in CMT for asset authoring preview — not shared via packages.
 * Keep visual parity with:
 * apps/admin/ui/src/app/pages/CampaignManagement/assets/components/preview/marketing-message/SideCardPreviewTemplate.tsx
 * (card frame and body markup live in MarketingWidgetTemplate.tsx in this folder)
 * Changes to the widget shell must be reflected in the CMT preview, and vice versa.
 */
import { useCallback, useEffect, useMemo } from 'react';
import Skeleton from '@/enterprise/components/Skeleton';
import { HOMEPAGE_SIDECARD_PLACEMENT, useMarketingArtifacts } from '@/enterprise/data/useArtifacts';
import useFeature from '@/lib/features';
import useDataDog from '@/reporting/DataDog';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import MarketingWidgetDeck, { MARKETING_WIDGET_DECK_LIMIT } from './MarketingWidgetDeck';
import MarketingWidgetTemplate, { MarketingWidgetTemplateProps } from './MarketingWidgetTemplate';
import useClientMarketingWidget, { useClientMarketingWidgets } from './useClientMarketingWidget';
import { mapMarketingArtifactToWidgetProps } from './utils';

export { HOMEPAGE_SIDECARD_PLACEMENT };

/** Module scope so `useMarketingArtifacts` can memoize its selection across renders. */
const SIDECARD_PLACEMENTS = [HOMEPAGE_SIDECARD_PLACEMENT];

/** Loading placeholder in the card frame. Used by both flag paths while artifacts or client widgets load. */
const MarketingWidgetSkeleton = () => (
  <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-blue-600 bg-gradient-to-b from-lightBlue-500/15 to-lightBlue-500/50 shadow-md">
    <div className="space-y-4 px-6 py-10">
      <Skeleton className="h-20 w-1/2" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-10 w-1/2" />
    </div>
  </div>
);

/** Flag off, no artifact: renders the single first-match client widget (fed rate, quarter end, lending, FinanceIQ, or How it works). */
const ClientMarketingWidgetContent = () => {
  const widgetProps = useClientMarketingWidget();

  return <MarketingWidgetTemplate {...widgetProps} />;
};

/** Flag off, no artifact: suspense boundary so the client widget's market queries show the skeleton while loading. */
const ClientMarketingWidget = () => (
  <QueryBoundaries LoadingComponent={MarketingWidgetSkeleton}>
    <ClientMarketingWidgetContent />
  </QueryBoundaries>
);

/** Both flag paths: up to three side-card artifacts from SEA, with fetch errors reported to Datadog. */
const useHomepageMarketingArtifacts = () => {
  const { addError } = useDataDog();
  const query = useMarketingArtifacts(SIDECARD_PLACEMENTS);
  const { isError, error } = query;

  useEffect(() => {
    if (isError && error) {
      addError(error, { context: 'enterprise::marketing-widget::getArtifacts' });
    }
  }, [isError, error, addError]);

  return query;
};

/**
 * Flag off: one card, today's behavior. The highest-priority artifact wins; with no artifact, falls back to the client widget.
 * Keep both mappers (`useClientMarketingWidget`, `mapMarketingArtifactToWidgetProps`) until client widgets
 * are fully replaced by campaigns.
 */
const MarketingWidgetContent = () => {
  const { data: marketingArtifacts, isLoading, isError } = useHomepageMarketingArtifacts();
  const selectedArtifact = marketingArtifacts?.[0];

  if (isLoading) {
    return <MarketingWidgetSkeleton />;
  }

  if (!isError && selectedArtifact) {
    return <MarketingWidgetTemplate {...mapMarketingArtifactToWidgetProps(selectedArtifact)} />;
  }

  return <ClientMarketingWidget />;
};

/** Flag on, fewer than three artifacts: appends eligible client widgets after the artifacts and cuts the deck at three. */
const ClientFilledMarketingWidgetDeck = ({ artifactWidgets }: { artifactWidgets: MarketingWidgetTemplateProps[] }) => {
  const clientWidgets = useClientMarketingWidgets();

  return <MarketingWidgetDeck widgets={[...artifactWidgets, ...clientWidgets].slice(0, MARKETING_WIDGET_DECK_LIMIT)} />;
};

/**
 * Flag on: builds the carousel. Artifacts come first by priority, then client widgets, with How it works last, capped at three.
 * Client widgets only load when fewer than three artifacts qualify, so a full artifact deck skips the client queries.
 */
const MarketingWidgetDeckContent = () => {
  const { data: marketingArtifacts, isLoading, isError } = useHomepageMarketingArtifacts();

  const artifactWidgets = useMemo(
    () => (isError ? [] : (marketingArtifacts ?? []).map(mapMarketingArtifactToWidgetProps)),
    [isError, marketingArtifacts]
  );

  /**
   * Stable across renders, because `QueryBoundaries` renders this as `<ErrorComponent />` — a new
   * function identity is a new component type, which remounts the deck and reports a second
   * `widget::carousel::shown` for one carousel. Changes only when the artifacts do.
   */
  const ArtifactOnlyDeck = useCallback(() => <MarketingWidgetDeck widgets={artifactWidgets} />, [artifactWidgets]);

  if (isLoading) {
    return <MarketingWidgetSkeleton />;
  }

  if (artifactWidgets.length >= MARKETING_WIDGET_DECK_LIMIT) {
    return <MarketingWidgetDeck widgets={artifactWidgets} />;
  }

  return (
    <QueryBoundaries LoadingComponent={MarketingWidgetSkeleton} ErrorComponent={ArtifactOnlyDeck}>
      <ClientFilledMarketingWidgetDeck artifactWidgets={artifactWidgets} />
    </QueryBoundaries>
  );
};

/** Homepage side card entry point. The carousel flag picks the carousel or today's single card. */
const MarketingWidget = () => {
  const [isCarouselEnabled] = useFeature('enterprise-ui_enableMarketingWidgetCarousel');

  if (isCarouselEnabled) {
    return <MarketingWidgetDeckContent />;
  }

  return <MarketingWidgetContent />;
};

export default MarketingWidget;
